import React from 'react'
import { Menu, MenuItem } from '@mui/material'
import { styled } from '@mui/material/styles'
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import { IconButton } from '@/design-system'

export interface ReportMenuProps {
  onReport: () => void
  /** Used only for the kebab's aria-label, e.g. "Sarah's wedding album". */
  itemLabel?: string
}

const Kebab = styled(IconButton)({
  position: 'absolute',
  top: 8,
  right: 8,
  zIndex: 4,
  width: 26,
  height: 26,
  background: 'rgba(13,13,15,0.5)',
  color: '#fff',
  opacity: 0,
  backdropFilter: 'blur(8px)',
  transition: `opacity var(--ds-duration-fast) var(--ds-ease), background var(--ds-duration-fast) var(--ds-ease)`,
  '&:hover': { background: 'rgba(13,13,15,0.75)' },
})

/**
 * Kebab + one-item "Report" menu overlaid on a card's cover — the safety
 * affordance a public feed needs that private "my stuff" grids don't, since
 * Discover is the first surface in the new shell showing strangers' content
 * to other strangers. Visual-only for now: `onReport` is left to the caller
 * (Discover dims the card locally) — no moderation queue wired up yet.
 *
 * Revealed on hover via the parent card's `&:hover .card-kebab` rule (see
 * AlbumCard/GalleryCard `Root`), or whenever its own menu is open, so it
 * doesn't disappear mid-click.
 */
export function ReportMenu({ onReport, itemLabel = 'this item' }: ReportMenuProps) {
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null)

  return (
    <>
      <Kebab
        className='card-kebab'
        size='small'
        aria-label={`More options for ${itemLabel}`}
        style={anchor ? { opacity: 1 } : undefined}
        onClick={(e) => {
          e.stopPropagation()
          setAnchor(e.currentTarget)
        }}
      >
        <MoreHorizIcon sx={{ fontSize: 16 }} />
      </Kebab>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          sx: {
            mt: 0.5,
            minWidth: 150,
            borderRadius: 'var(--ds-radius-md)',
            boxShadow: 'var(--ds-shadow-soft)',
            border: '1px solid var(--ds-border)',
          },
        }}
      >
        <MenuItem
          onClick={(e) => {
            e.stopPropagation()
            setAnchor(null)
            onReport()
          }}
          sx={{ fontSize: 13, display: 'flex', gap: 1, color: 'var(--ds-danger)' }}
        >
          <FlagOutlinedIcon sx={{ fontSize: 16 }} /> Report
        </MenuItem>
      </Menu>
    </>
  )
}
